import React from 'react';
import {
  Card, CardContent, CardHeader, Typography, Box, Chip, Tooltip,
  Paper, CircularProgress, LinearProgress, Grid
} from '@mui/material';
import {
  EmojiEvents,
  Restaurant,
  ShoppingBag,
  TheaterComedy,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
} from '@mui/icons-material';

/**
 * Get color based on score
 */
const getScoreColor = (score) => {
  if (score >= 80) return '#2e7d32'; // green
  if (score >= 60) return '#1976d2'; // blue
  if (score >= 40) return '#ed6c02'; // orange
  return '#d32f2f'; // red
};

/**
 * Get status chip color
 */
const getStatusChipColor = (status) => {
  switch (status) {
    case 'excellent': return 'success';
    case 'good': return 'info';
    case 'moderate': return 'warning';
    case 'needs_attention': return 'error';
    default: return 'default';
  }
};

/**
 * Get icon for category
 */
const getCategoryIcon = (categoryName) => {
  switch (categoryName) {
    case 'Eating out & delivery':
      case 'Outside Food': return <Restaurant sx={{ fontSize: 20 }} />;
    case 'General shopping':
      case 'Shopping': return <ShoppingBag sx={{ fontSize: 20 }} />;
    case 'Entertainment': return <TheaterComedy sx={{ fontSize: 20 }} />;
    default: return null;
  }
};

/**
 * Get trend icon based on score comparison
 */
const getTrendIcon = (currentScore, previousScore) => {
  if (previousScore === undefined || previousScore === null) return null;
  const diff = currentScore - previousScore;
  if (diff > 5) return <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />;
  if (diff < -5) return <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />;
  return <TrendingFlat sx={{ fontSize: 16, color: 'text.secondary' }} />;
};

/**
 * MonthHabitsCard - Single month's spending habits card
 */
const MonthHabitsCard = ({ monthData, previousMonthData, isFirst }) => {
  const habits = monthData.habits;
  if (!habits) return null;

  return (
    <Paper
      elevation={monthData.isCurrent ? 3 : 1}
      sx={{
        p: 2,
        height: '100%',
        border: monthData.isCurrent ? '2px solid' : '1px solid',
        borderColor: monthData.isCurrent ? 'primary.main' : 'divider',
        backgroundColor: monthData.isCurrent ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Month Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography 
            variant="subtitle1" 
            fontWeight={monthData.isCurrent ? 700 : 500}
            color={monthData.isCurrent ? 'primary.main' : 'text.primary'}
          >
            {monthData.fullLabel}
          </Typography>
          {monthData.isCurrent && (
            <Chip label="Current" size="small" color="primary" sx={{ height: 18, fontSize: '0.6rem' }} />
          )}
        </Box>
        
        {/* Overall Score Circle */}
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant="determinate"
            value={100}
            size={50}
            thickness={4}
            sx={{ color: 'rgba(0,0,0,0.08)' }}
          />
          <CircularProgress
            variant="determinate"
            value={habits.overallScore}
            size={50}
            thickness={4}
            sx={{
              color: getScoreColor(habits.overallScore),
              position: 'absolute',
              left: 0,
            }}
          />
          <Box
            sx={{
              top: 0, left: 0, bottom: 0, right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography 
              variant="body2" 
              fontWeight="bold"
              sx={{ color: getScoreColor(habits.overallScore) }}
            >
              {habits.overallScore}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Status */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip 
          size="small"
          label={habits.overallStatus?.replace('_', ' ') || 'N/A'}
          color={getStatusChipColor(habits.overallStatus)}
          sx={{ textTransform: 'capitalize', height: 22 }}
        />
        {previousMonthData?.habits && getTrendIcon(habits.overallScore, previousMonthData.habits.overallScore)}
      </Box>

      {/* Category Breakdown */}
      {habits.categories?.map((cat) => {
        const prevCat = previousMonthData?.habits?.categories?.find(c => c.name === cat.name);
        return (
          <Box key={cat.name} sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ color: getScoreColor(cat.score) }}>
                  {getCategoryIcon(cat.name)}
                </Box>
                <Typography variant="body2" fontWeight={500}>
                  {cat.name}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip 
                  label={cat.score}
                  size="small"
                  sx={{ 
                    bgcolor: getScoreColor(cat.score),
                    color: 'white',
                    fontWeight: 'bold',
                    minWidth: 36,
                    height: 22
                  }}
                />
                {prevCat && getTrendIcon(cat.score, prevCat.score)}
              </Box>
            </Box>
            
            <LinearProgress 
              variant="determinate" 
              value={cat.score} 
              sx={{ 
                height: 5, 
                borderRadius: 3,
                mb: 0.5,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  bgcolor: getScoreColor(cat.score),
                  borderRadius: 3
                }
              }}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
              <Box 
                sx={{ 
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: cat.count > cat.ideal ? '#ffebee' : cat.count > cat.ideal * 0.75 ? '#fff3e0' : '#e8f5e9',
                  border: '1px solid',
                  borderColor: cat.count > cat.ideal ? '#f44336' : cat.count > cat.ideal * 0.75 ? '#ff9800' : '#4caf50',
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 700,
                    color: cat.count > cat.ideal ? '#c62828' : cat.count > cat.ideal * 0.75 ? '#e65100' : '#2e7d32',
                  }}
                >
                  {cat.count} txn{cat.count !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Target: ≤{cat.ideal}/mo
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Paper>
  );
};

/**
 * SpendingHabitsTable - 3-month card view of spending habits
 */
const SpendingHabitsTable = ({ habitsHistory, currentSpendingHabits, loading }) => {
  if (loading) {
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading spending habits...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // If no history, return null (will fall back to original view in Dashboard)
  if (!habitsHistory || habitsHistory.length === 0) {
    return null;
  }

  // Get current month's overall data
  const currentOverall = currentSpendingHabits || habitsHistory[0]?.habits;

  return (
    <Card sx={{ mb: 4 }}>
      <CardHeader 
        title="Spending Habits Score (3-Month View)" 
        titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
        subheader={currentOverall?.overallMessage || 'Track your discretionary spending'}
        sx={{ pb: 0 }}
        avatar={
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              value={currentOverall?.overallScore || 0}
              size={56}
              thickness={4}
              sx={{ color: getScoreColor(currentOverall?.overallScore || 0) }}
            />
            <Box
              sx={{
                top: 0, left: 0, bottom: 0, right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography 
                variant="body2" 
                fontWeight="bold" 
                sx={{ color: getScoreColor(currentOverall?.overallScore || 0) }}
              >
                {currentOverall?.overallScore || 0}
              </Typography>
            </Box>
          </Box>
        }
        action={
          currentOverall && (
            <Chip 
              icon={<EmojiEvents />}
              label={currentOverall.overallStatus?.replace('_', ' ') || 'N/A'}
              color={getStatusChipColor(currentOverall.overallStatus)}
              sx={{ textTransform: 'capitalize' }}
            />
          )
        }
      />
      <CardContent sx={{ pt: 3 }}>
        {/* 3 Month Cards - Current first, then backwards - wider layout */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {habitsHistory.map((monthData, index) => (
            <Box 
              key={`${monthData.year}-${monthData.month}`}
              sx={{ flex: '1 1 300px', minWidth: 280, maxWidth: 450 }}
            >
              <MonthHabitsCard 
                monthData={monthData}
                previousMonthData={habitsHistory[index + 1]} // Previous month for trend comparison
                isFirst={index === 0}
              />
            </Box>
          ))}
        </Box>

        {/* Current month tips */}
        {currentOverall?.categories && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
              TIPS FOR THIS MONTH
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {currentOverall.categories.map((cat) => (
                <Tooltip key={cat.name} title={cat.tip} arrow>
                  <Chip
                    icon={getCategoryIcon(cat.name)}
                    label={cat.tip.length > 35 ? cat.tip.substring(0, 35) + '...' : cat.tip}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      borderColor: getScoreColor(cat.score),
                      cursor: 'help',
                      '& .MuiChip-icon': { color: getScoreColor(cat.score) }
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SpendingHabitsTable;
